import React from 'react';
import { cdnUrl, buildSrcSet, getVariantDimensions, VARIANTS } from '../lib/cdn';

/**
 * <Img> — drop-in replacement for <img> with:
 *   - CDN URL transformation (cdnUrl + buildSrcSet)
 *   - Explicit width/height attributes (prevents Cumulative Layout Shift)
 *   - loading="lazy" by default; loading="eager" + fetchpriority="high" when priority
 *   - decoding="async"
 *   - srcset for 1x/2x display
 *
 * Props:
 *   - src (required) — source URL
 *   - alt (required) — accessibility; pass "" for decorative images
 *   - variant — one of VARIANTS keys (og, hero, heroNarrow, card, thumb, square, avatar)
 *   - sizes — explicit sizes attribute (defaults to a sensible variant-based hint)
 *   - priority — true for above-the-fold images; sets loading=eager + fetchpriority=high
 *   - aspectRatio — Tailwind class override; falls back to the variant's intrinsic ratio
 *   - className, style, ...rest — passed through to <img>
 */
export const Img = ({
  src,
  alt = '',
  variant = 'card',
  sizes,
  priority = false,
  className = '',
  style,
  ...rest
}) => {
  if (!src) return null;

  const v = VARIANTS[variant] || VARIANTS.card;
  const { width, height } = getVariantDimensions(variant);
  const srcSet = buildSrcSet(src, variant);
  const finalSrc = cdnUrl(src, v);

  // Default `sizes` hint that mirrors the variant scale.
  const defaultSizes = (
    variant === 'hero' ? '100vw'
    : variant === 'heroNarrow' ? '(min-width: 768px) 70vw, 100vw'
    : variant === 'card' ? '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'
    : variant === 'thumb' ? '(min-width: 768px) 25vw, 50vw'
    : variant === 'square' ? '(min-width: 768px) 33vw, 100vw'
    : '100vw'
  );

  return (
    <img
      src={finalSrc}
      srcSet={srcSet}
      sizes={sizes || defaultSizes}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={priority ? 'high' : 'auto'}
      className={className}
      style={style}
      {...rest}
    />
  );
};

export default Img;
